import { LogIn, Menu, ShieldCheck, X } from "lucide-react";
import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import MarketTicker from "./MarketTicker";

interface AppHeaderProps {
  brandText?: string;
  rightSlot?: React.ReactNode;
}

const AppHeader: React.FC<AppHeaderProps> = ({ brandText = "Tylee Smart Markets", rightSlot }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [location] = useLocation();

  const navCls = (href: string, extra?: string) => {
    const isActive = location === href || (href === "/video" && location.startsWith("/video/"));
    const active = isActive ? " nav-active" : "";
    return extra ? `${extra}${active}` : active || undefined;
  };
  const navProps = (href: string, extra?: string) => {
    const isActive = location === href || (href === "/video" && location.startsWith("/video/"));
    return {
      className: navCls(href, extra),
      "aria-current": isActive ? ("page" as const) : undefined,
    };
  };

  return (
    <header aria-label="Tylee Smart Markets">
      <div className="home-topbar">
        <Link href="/" className="home-brand" aria-label="Tylee Smart Markets home">
          <img
            src="/static/assets/images/TSM_2026.png"
            alt={brandText}
            className="home-brand-logo"
          />
        </Link>

        <nav className="home-nav" aria-label="Primary">
          <Link href="/" {...navProps("/")}>Home</Link>
          <Link href="/news" {...navProps("/news")}>News</Link>
          <Link href="/monitor" {...navProps("/monitor")}>Monitor</Link>
          <Link href="/video" {...navProps("/video")}>Video</Link>
          <Link href="/alerts" {...navProps("/alerts")}>Alerts</Link>
          <Link href="/analytics" {...navProps("/analytics")}>Analytics</Link>
          <Link href="/reports" {...navProps("/reports")}>Reports</Link>
          <Link href="/maps" {...navProps("/maps")}>Maps</Link>
          <Link href="/radar" {...navProps("/radar")}>Radar</Link>
          <span className="home-nav-divider" aria-hidden="true" />
          <Link href="/brief" {...navProps("/brief", "nav-util")}>Brief</Link>
          <Link href="/help" {...navProps("/help", "nav-util")}>Help</Link>
          <Link href="/get-started" {...navProps("/get-started", "nav-util")}>Keys</Link>
          <Link href="/faq" {...navProps("/faq", "nav-util")}>FAQ</Link>
          <Link href="/status" {...navProps("/status", "nav-util")}>Status</Link>
        </nav>

        <div className="home-auth-actions" aria-label="Account">
          {rightSlot ?? (
            <>
              <Link href="/get-started" className="home-icon-button" aria-label="Manage API keys">
                <ShieldCheck size={18} aria-hidden="true" />
              </Link>
              <button className="home-login-button" type="button">
                <LogIn size={16} aria-hidden="true" />
                <span>Login</span>
              </button>
            </>
          )}
        </div>

        <button
          className="home-mobile-toggle"
          type="button"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="tsm-mobile-primary-nav"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
        </button>
      </div>

      {mobileOpen && (
        <nav id="tsm-mobile-primary-nav" className="home-mobile-nav" aria-label="Mobile Primary">
          <Link href="/" {...navProps("/")} onClick={() => setMobileOpen(false)}>Home</Link>
          <Link href="/news" {...navProps("/news")} onClick={() => setMobileOpen(false)}>News</Link>
          <Link href="/monitor" {...navProps("/monitor")} onClick={() => setMobileOpen(false)}>Monitor</Link>
          <Link href="/video" {...navProps("/video")} onClick={() => setMobileOpen(false)}>Video</Link>
          <Link href="/alerts" {...navProps("/alerts")} onClick={() => setMobileOpen(false)}>Alerts</Link>
          <Link href="/analytics" {...navProps("/analytics")} onClick={() => setMobileOpen(false)}>Analytics</Link>
          <Link href="/reports" {...navProps("/reports")} onClick={() => setMobileOpen(false)}>Reports</Link>
          <Link href="/maps" {...navProps("/maps")} onClick={() => setMobileOpen(false)}>Maps</Link>
          <Link href="/radar" {...navProps("/radar")} onClick={() => setMobileOpen(false)}>Radar</Link>
          <Link href="/brief" {...navProps("/brief")} onClick={() => setMobileOpen(false)}>Brief</Link>
          <Link href="/help" {...navProps("/help")} onClick={() => setMobileOpen(false)}>Help</Link>
          <Link href="/get-started" {...navProps("/get-started")} onClick={() => setMobileOpen(false)}>Keys</Link>
          <Link href="/faq" {...navProps("/faq")} onClick={() => setMobileOpen(false)}>FAQ</Link>
          <Link href="/status" {...navProps("/status")} onClick={() => setMobileOpen(false)}>Status</Link>
        </nav>
      )}

      <MarketTicker />
    </header>
  );
};

export default AppHeader;
